<template>
  <div>
    <form v-on:submit.prevent="submitForm">
      <div class="form-group">
        <label for="title" class="wfh-label">To-do task title</label>
        <div class="input-group mb-3">
          <input
            type="text"
            id="title"
            name="title"
            class="
              form-control
              wfh-input-border wfh-input-padding wfh-input-text
              border-right-0
            "
            placeholder="Type task title"
            v-model="task.title"
            required
          />
          <div class="input-group-append">
            <div class="input-group-text bg-white border-left-0">
              <checkbox
                :type="checkboxType(task.is_completed)"
                @clicked="task.is_completed = !task.is_completed"
              ></checkbox>
            </div>
          </div>
        </div>
      </div>

      <div class="form-group">
        <label for="description" class="wfh-label">Task description</label>
        <textarea
          id="description"
          name="description"
          rows="3"
          class="form-control wfh-input-border wfh-input-text"
          v-model="task.description"
        ></textarea>
      </div>

      <div class="row">
        <div class="col">
          <label class="wfh-label"
            >Sub-tasks <span class="small">- Optional</span></label
          >
          <div class="form-group" v-for="(subtask, index) in task.subtasks">
            <div class="input-group mb-3">
              <input
                type="text"
                ref="subtask"
                class="
                  form-control
                  wfh-input-border wfh-input-padding wfh-input-text
                  border-right-0
                "
                :aria-label="`Subtask ${index}`"
                placeholder="Type a subtask"
                v-model="subtask.title"
                @keypress.enter.prevent="addNewSubTasks"
              />
              <div class="input-group-append">
                <div class="input-group-text bg-white border-left-0">
                  <checkbox
                    :type="checkboxType(subtask.is_completed)"
                    @clicked="subtask.is_completed = !subtask.is_completed"
                  ></checkbox>
                </div>
              </div>
            </div>

            <div class="row no-gutters">
              <div class="col-auto ml-auto">
                <button
                  type="button"
                  class="btn btn-link font-weight-bold text-grey pr-0"
                  @click="removeSubTask(index)"
                  v-show="index || (!index && task.subtasks.length > 1)"
                >
                  Remove
                </button>
                <button
                  type="button"
                  class="btn btn-link font-weight-bold text-grey-02 pr-0"
                  @click="addNewSubTasks"
                  v-show="index === task.subtasks.length - 1"
                >
                  <AddPlusIcon />
                  Add
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="form-group">
        <label class="wfh-label"
          >Choose a label <span class="small">- Or type a new one</span></label
        >
        <Selector
          :required="false"
          :options="labelOptions"
          :selected="task.label_id"
          v-on:resetValue="task.label_id = null"
          v-on:selectItem="handleLabelSelection"
          v-on:updateLabel="updateLabel"
          v-on:deleteItem="deleteLabel"
        />
      </div>

      <div class="form-group" v-if="!isExternalMember">
        <label for="assign-type" class="wfh-label">Choose Assign Type</label>
        <div class="wfh-v-select-override">
          <v-select
            id="assign-type"
            :options="typeOptions"
            :filterable="false"
            :clearable="false"
            v-model="assignedType"
            :reduce="(item) => item.value"
          >
          </v-select>
        </div>
      </div>

      <div class="form-group" v-if="isExternalMember">
        <label class="wfh-label">Assign Task To Company</label>
        <div class="wfh-v-select-override">
          <v-select
            :options="userCompanies"
            label="name"
            :clearable="false"
            v-model="task.company"
          >
          </v-select>
        </div>
      </div>

      <div
        class="form-group"
        v-if="assignedType === 'individual' && !isExternalMember"
      >
        <label class="wfh-label">Assign task to individual</label>
        <div class="wfh-v-select-override">
          <v-select
            @input="verifyAssignation"
            :options="staffs"
            :multiple="true"
            @search="executeSearch"
            label="name"
            :filterable="false"
            :clearable="false"
            v-model="task.assignees"
          >
            <template slot="option" slot-scope="option">
              <div class="d-center">
                {{ option.name }}
              </div>
            </template>
            <template #search="{ attributes, events }">
              <input
                class="vs__search"
                :required="task.assignees.length === 0"
                v-bind="attributes"
                v-on="events"
              />
            </template>
          </v-select>
        </div>
      </div>

      <div
        class="form-group"
        v-if="assignedType === 'team' && !isExternalMember"
      >
        <label class="wfh-label">Assign Task To Team</label>
        <div class="wfh-v-select-override">
          <v-select
            :options="teamOptions"
            :filterable="false"
            :clearable="false"
            v-model="task.team_id"
            :reduce="(item) => item.code"
          >
            <template #search="{ attributes, events }">
              <input
                class="vs__search"
                :required="!task.team_id"
                v-bind="attributes"
                v-on="events"
              />
            </template>
          </v-select>
        </div>
      </div>

      <div class="form-group">
        <label class="wfh-label"> Need to be completed by </label>
        <div class="position-relative d-flex wfh-input-override">
          <v-ctk-datepicker
            format="YYYY-MM-DD"
            formatted="YYYY-MM-DD"
            v-model="task.estimated_completion_date"
            :no-button-now="true"
            :auto-close="true"
            :no-label="true"
            :only-date="true"
          />
          <img
            v-bind:src="'/images/date.svg'"
            class="position-absolute align-self-center"
            style="right: 8px"
          />
        </div>
      </div>

      <div class="py-4">
        <div class="row">
          <div class="col">
            <div class="subtitle">
              <h2>Custom Fields</h2>
              <p>Make custom unique tracker for your task</p>
            </div>
          </div>
        </div>

        <div class="row mb-3">
          <div class="col-auto ml-auto">
            <button
              v-if="
                this.defaultCustomAttributes.length > 0 &&
                changesMadeToCustomAttributes
              "
              type="button"
              class="btn btn-link text-right font-weight-bold text-grey-02"
              @click="reloadDefaultCustomAttributes"
            >
              <i class="fas fa-sync-alt mr-2" style="color: #0096f5"></i>
              Reload Initial Fields
            </button>
            <button
              type="button"
              class="btn btn-link text-right font-weight-bold text-grey-02"
              @click="openCustomFieldModal"
            >
              <i class="fas fa-plus mr-2" style="color: #0096f5"></i>
              Add Custom Fields
            </button>
          </div>
        </div>

        <div class="row" v-if="task.custom_attributes.length > 0">
          <div
            class="col-12 col-md-6"
            v-for="(customField, key) of task.custom_attributes"
          >
            <div class="form-group">
              <label class="wfh-label">
                {{ customField.label }}
                {{ customField.searchable ? "(Searchable)" : "" }}
              </label>
              <switchable-input
                v-model="customField.value"
                :type="`${customField.type}`"
                :options="customField.options"
              ></switchable-input>
            </div>

            <div class="row no-gutters">
              <div class="col-auto ml-auto">
                <button
                  type="button"
                  class="btn wfh-btn-link-primary font-weight-bold pr-0"
                  @click="(event) => openCustomFieldModal(event, key)"
                >
                  Edit
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="row" v-else>
          <div class="col-auto mx-auto">No custom fields selected</div>
        </div>
      </div>

      <task-comments
        :taskId="task_id"
        @changeComments="updateNewComments"
        @input="commentHasInput"
      ></task-comments>

      <div class="d-flex justify-content-center">
        <div class="col-6 d-flex justify-content-center">
          <button
            type="button"
            class="
              btn btn-block
              btn-outline-dark wfh-btn-large
              position-relative
            "
            :disabled="isLoading"
            @click="promptTaskDeletion = true"
          >
            <span
              class="spinner-border position-absolute"
              role="status"
              style="left: 16px; top: 14px"
              v-if="isLoading"
            >
              <span class="sr-only">Loading...</span>
            </span>
            Delete
          </button>
        </div>

        <div class="col-6 d-flex justify-content-center">
          <button
            type="submit"
            class="
              btn btn-block
              wfh-btn-color-primary wfh-btn-large
              position-relative
            "
            :disabled="isLoading"
          >
            <span
              class="spinner-border position-absolute"
              role="status"
              style="left: 16px; top: 14px"
              v-if="isLoading"
            >
              <span class="sr-only">Loading...</span>
            </span>
            Done
          </button>
        </div>

        <div
          class="ml-3"
          v-if="action === 'for_approval'"
          style="min-width: 240px"
        >
          <div class="dropdown">
            <button
              class="
                btn btn-block
                wfh-btn-color-secondary wfh-btn-large
                position-relative
              "
              type="button"
              id="dropdownMenuButton"
              data-toggle="dropdown"
              aria-haspopup="true"
              aria-expanded="false"
            >
              Action
              <i
                class="fa fa-chevron-down position-absolute"
                style="right: 8px; padding-top: 2px"
              ></i>
            </button>
            <div class="dropdown-menu" aria-labelledby="dropdownMenuButton">
              <button class="dropdown-item" type="button" @click="approveTask">
                Approve
              </button>
              <button class="dropdown-item" type="button" @click="rejectTask">
                Reject
              </button>
            </div>
          </div>
        </div>
      </div>
    </form>

    <task-modal-remove
      v-if="promptTaskDeletion"
      @clickDelete="deleteTask"
      @close="promptTaskDeletion = false"
    />

    <transition name="fade">
      <custom-field-modal
        v-if="showCustomFieldModal"
        :selectedCustomField="customFieldToEdit"
        @submit="handleCustomFieldSubmit"
        @close="closeCustomFieldModal"
        @delete="deleteCustomField"
        @update="updateCustomFields"
      ></custom-field-modal>
    </transition>
  </div>
</template>

<script>
import VueTypeaheadBootstrap from "vue-typeahead-bootstrap"
import vSelect from "vue-select"
import _ from "lodash"
import SelectorItem from "../../Misc/CustomSelector/SelectorItem"
import Selector from "../../Misc/CustomSelector/Selector"
import Checkbox from "../../../atoms/Tasks/Checkbox"
import AddPlusIcon from "../../../icons/AddPlusIcon"
import { getAction } from "../../../services/GetAction"
import { approveTask, rejectTask, update, destroy } from "../../../services/taskService"
import Swal from "sweetalert2"

export default {
  components: {
    AddPlusIcon,
    Checkbox,
    Selector,
    SelectorItem,
    VueTypeaheadBootstrap,
    vSelect,
    getAction,
    approveTask,
    rejectTask,
  },

  props: ["task_title", "task_type", "completed_at", "task_id", "company"],

  data() {
    return {
      task: {
        title: this.task_title ? decodeURI(this.task_title) : null,
        description: null,
        subtasks: [
          {
            title: null,
            is_completed: false,
          },
        ],
        label_id: null,
        is_completed: !!this.completed_at,
        estimated_completion_date: null,
        assignees: [],
        team_id: null,
        team_name: null,
        completed_at: this.completed_at,
        company: null,
        custom_attributes: [],
      },
      defaultCustomAttributes: [],
      changesMadeToCustomAttributes: false,
      deletedSubtasks: [],
      counterSubTask: 1,
      staffs: [],
      label_options: [],
      addingLabels: false,
      selectedCountries: [],
      countries: [],
      isLoading: false,
      teams: [],
      assignedType: null,
      typeOptions: [
        {
          label: "Individual Task",
          value: "individual",
        },
        {
          label: "Team",
          value: "team",
        },
      ],
      userCompanies: [],
      showCustomFieldModal: false,
      customFieldToEdit: null,
      customFieldToEditKey: null,
      newComments: [],
      commentInput: "",
      action: null,
      promptTaskDeletion: false,
    }
  },

  computed: {
    currentUser() {
      return this.$store.state.currentUser
    },

    isExternalMember() {
      return this.$store.state.isExternal
    },

    timezone() {
      return this.$store.state.timezone
    },

    teamOptions() {
      const teams = this.teams.map((item) => {
        return {
          label: item.name,
          code: item.id,
        }
      })

      if (teams.length === 1 && !this.task.id) {
        this.task.team_id = teams[0].code
      }

      return teams
    },

    labelOptions() {
      return this.$store.state.labelOptions
    },

    returnUrl() {
      let hash = ""

      if (this.isExternalMember && !this.task_id) {
        hash = "#pending"
      }

      return `/dashboard${hash}`
    },
  },

  watch: {
    "task.assignees"() {
      this.teams = []

      if (this.task.assignees.length === 0) {
        this.getUserGroup(this.currentUser.id)
      } else {
        this.task.assignees.forEach((item) => {
          this.getUserGroup(item.id)
        })
      }
    },

    currentUser() {
      this.addCurrentUserToTaskUsers()
    },

    assignedType() {
      if (this.assignedType === "team") {
        this.task.assignees = []
      } else {
        this.addCurrentUserToTaskUsers()
        this.task.team_id = null
      }
    },

    showCustomFieldModal() {
      if (this.showCustomFieldModal === true) {
        document.body.style.top = `-${window.scrollY}px`
        document.body.style.position = "fixed"

        return
      }

      if (this.showCustomFieldModal === false) {
        const scrollY = document.body.style.top

        document.body.style.position = ""
        document.body.style.top = ""
        window.scrollTo(0, parseInt(scrollY || "0") * -1)
      }
    },
  },

  methods: {
    reloadDefaultCustomAttributes() {
      this.changesMadeToCustomAttributes = false
      this.task.custom_attributes = this.defaultCustomAttributes.slice()
    },

    handleLabelSelection(val, id) {
      this.task.label_id = id
      this.changesMadeToCustomAttributes = true
      this.fetchLabelCustomAttributes()
    },

    async fetchLabelCustomAttributes() {
      const res = await axios.get(
        `/labels/${this.task.label_id}/custom-attributes`
      )

      this.task.custom_attributes = res.data.data.map((custom_attribute) => {
        return {
          id: custom_attribute.id,
          label: custom_attribute.label,
          type: custom_attribute.type,
          value: "",
          options: custom_attribute.options,
          searchable: custom_attribute.searchable,
        }
      })

      if (this.defaultCustomAttributes.length === 0) {
        this.defaultCustomAttributes = this.task.custom_attributes.slice()
      }
    },

    checkboxType(isCompleted) {
      return isCompleted ? "primary" : ""
    },

    handleCustomFieldSubmit(customField) {
      if (this.customFieldToEditKey !== null) {
        this.task.custom_attributes[this.customFieldToEditKey] = customField
      } else {
        this.task.custom_attributes.push(customField)
      }

      this.changesMadeToCustomAttributes = true
      this.closeCustomFieldModal()
    },

    resetSelectedField() {
      this.customFieldToEditKey = null
      this.customFieldToEdit = null
    },

    openCustomFieldModal(event, selectedCustomFieldIndex = null) {
      if (selectedCustomFieldIndex !== null) {
        this.customFieldToEditKey = selectedCustomFieldIndex
        this.customFieldToEdit =
          this.task.custom_attributes[selectedCustomFieldIndex]
      }

      this.showCustomFieldModal = true
    },

    closeCustomFieldModal() {
      this.resetSelectedField()
      this.showCustomFieldModal = false
    },

    deleteCustomField() {
      if (this.customFieldToEditKey !== null) {
        this.task.custom_attributes.splice(this.customFieldToEditKey, 1)
        this.closeCustomFieldModal()
        this.changesMadeToCustomAttributes = true
      }
    },

    executeSearch: _.debounce(async function (queryString) {
      await this.getStaffs(queryString)
    }, 500),

    async getTask(taskId) {
      const res = await axios.get(`/tasks/${taskId}`)
      const task = res.data.data

      if (task.team_id !== null && !this.taskCreatedByCurrentUser(task)) {
        this.assignedType = "team"
      } else {
        this.assignedType = "individual"
      }

      this.task.id = task.id
      this.task.title = task.title
      this.task.type = task.type
      this.task.description = task.description
      this.task.completed_at = task.completed_at
      this.task.team_id = task.team_id
      this.task.label_id = task.labels[0]?.id ?? null
      this.task.assignees = task.users

      this.task.subtasks = task.subtasks.map((subtask) => {
        subtask["is_completed"] = !!(
          subtask["completed_at"] || subtask["is_completed"]
        )

        return subtask
      })

      if (this.task.subtasks.length === 0) {
        this.task.subtasks = [
          {
            title: null,
            is_completed: false,
          },
        ]
      }

      this.parseTaskCustomAttribute(task)
    },

    async getStaffs(queryString) {
      const res = await axios.get(
        "/team-members" + (queryString ? `?q=${queryString}` : "")
      )
      this.staffs = res.data.data
    },

    addCurrentUserToTaskUsers() {
      if (!this.currentUser.id) return

      if (this.task.assignees.length === 0) {
        this.task.assignees.push(this.currentUser)
      }

      this.getUserGroup(this.currentUser.id)
    },

    verifyAssignation(users) {
      const initialTaskUser = users.slice()

      if (initialTaskUser.length > 1) {
        let verifiedTaskUser = initialTaskUser.slice()
        verifiedTaskUser.pop()

        const verificationValue = initialTaskUser.filter((user) => {
          return user.role
            ? user.role[0].slug === "freelancer"
            : user.role_slug === "freelancer"
        }).length

        if (
          verificationValue !== 0 &&
          verificationValue !== initialTaskUser.length
        ) {
          Vue.$toast.error(
            "You may select either freelancers or team members",
            {
              position: "top-right",
            }
          )
          this.task.assignees = verifiedTaskUser
        }
      }
    },

    addNewSubTasks() {
      this.task.subtasks.push({
        title: null,
        is_completed: false,
      })

      this.$nextTick(() => {
        this.$refs[`subtask`][this.$refs[`subtask`].length - 1].focus()
      })
    },

    async removeSubTask(index) {
      const subtask = this.task.subtasks[index]

      if (subtask.hasOwnProperty("id")) {
        await axios.delete(`/tasks/${this.task.id}/subtasks/${subtask.id}`)
      }

      this.task.subtasks.splice(index, 1)
    },

    async submitForm() {
      this.isLoading = true

      const existingSubtasks = this.task.subtasks.filter((subtask) => {
        return subtask.id !== null && typeof subtask.id !== "undefined"
      })
      const newSubtasks = this.task.subtasks.filter(
        (subtask) => !!subtask.title && !subtask.id
      )
      const assignees = this.task.assignees.map((assignee) => assignee.id)
      const payloadKeys = [
        "description",
        "completed_at",
        "is_completed",
        "label_id",
      ]
      const taskRequest = {
        type: this.task_type,
        title: this.task.title,
      }

      for (const key of payloadKeys) {
        const value = this.task[key]

        if (value) {
          taskRequest[key] = value
        }
      }

      if (this.task.team_id) {
        taskRequest["team_id"] = this.task.team_id
      } else {
        taskRequest["assignees"] = assignees
      }

      if (this.isExternalMember) {
        taskRequest["requires_approval"] = true
        taskRequest["company_id"] = this.task.company.id
      }

      if (this.task.custom_attributes.length > 0) {
        taskRequest["custom_attributes"] = this.task.custom_attributes.map(
          (custom_attribute) => {
            if (custom_attribute.hasOwnProperty("id")) {
              return {
                id: custom_attribute.id,
                value: custom_attribute.value,
              }
            }

            return {
              label: custom_attribute.label,
              type: custom_attribute.type,
              value: custom_attribute.value,
              options: custom_attribute.options,
              searchable: custom_attribute.searchable,
            }
          }
        )
      }

      if (this.newComments.length > 0) {
        taskRequest["comments"] = this.newComments.map((comment) => {
          return comment.content
        })
      }

      if (this.commentInput != "") {
        taskRequest["comment"] = this.commentInput
      }

      try {
        let taskId = this.task.id

        if (this.task.id) {
          await axios.put(`/tasks/${this.task.id}`, taskRequest)
        } else {
          const res = await axios.post("/tasks", taskRequest)

          taskId = res.data.data.id
        }

        if (newSubtasks.length > 0) {
          await axios.post(`/tasks/${taskId}/subtasks`, {
            subtasks: newSubtasks,
          })
        }

        if (existingSubtasks.length > 0) {
          await axios.put(`/tasks/${taskId}/subtasks`, {
            subtasks: existingSubtasks,
          })
        }

        this.isLoading = false
        window.location.href = this.returnUrl
      } catch (e) {
        this.isLoading = false
      }
    },

    async updateLabel(val, labelId) {
      let label = null
      let isNewLabel = true

      if (labelId) {
        const res = await axios.patch(
          `/company/${this.currentUser.company.id}/labels/${labelId}`,
          { label: val }
        )
        label = res.data.data
      } else {
        const res = await axios.post(
          `/company/${this.currentUser.company.id}/labels`,
          { label: val }
        )
        label = res.data.data
      }

      this.$store.state.labelOptions = this.labelOptions.map((value) => {
        if (label.id === value.id) {
          isNewLabel = false
          return label
        }

        return value
      })

      if (isNewLabel) {
        this.$store.state.labelOptions.push(label)
      }
    },

    async deleteLabel(labelId) {
      await axios.delete(
        `/company/${this.currentUser.company.id}/labels/${labelId}`
      )

      this.$store.state.labelOptions = this.labelOptions.filter((value) => {
        return value.id !== labelId
      })
    },

    async getUserGroup(userId) {
      await axios.get(`/users/${userId}/groups`).then((data) => {
        data.data.data.forEach((item) => {
          const group = _.find(this.teams, {
            id: item.id,
          })

          if (!group) {
            this.teams.push(item)
          }
        })
      })
    },

    async getUserCompanies() {
      const res = await axios.get("/get-user-companies")

      this.userCompanies = res.data.data

      if (this.userCompanies.length === 1) {
        this.task.company = this.userCompanies[0]
      }
    },

    updateNewComments(comments) {
      this.newComments = comments
    },

    commentHasInput(comment) {
      this.commentInput = comment
    },

    taskCreatedByCurrentUser(task) {
      return task.created_by === this.currentUser.id
    },

    async getAction() {
      const url = new URL(window.location.href)
      const actionHash = url.searchParams.get("action")

      if (actionHash !== null) {
        this.action = await getAction(actionHash)
      }
    },

    async approveTask() {
      const res = await approveTask(this.task.id)
      this.task = res.data.data

      Swal.fire({
        icon: "success",
        title: "Task has been approved",
      })
    },

    async rejectTask() {
      const res = await rejectTask(this.task.id)
      this.task = res.data.data

      Swal.fire({
        icon: "success",
        title: "Task has been rejected",
      })
    },

    async updateCustomFields(custom_attribute) {
      if (this.customFieldToEditKey !== null) {
        const res = await update(this.task, {
          custom_attributes: this.task.custom_attributes.map((attr, index) => {
            if (index === this.customFieldToEditKey) {
              attr = { ...attr, ...custom_attribute }
            }

            return attr
          })
        })

        this.parseTaskCustomAttribute(res.data.data)
        this.closeCustomFieldModal()

        Swal.fire({
          icon: "success",
          title: `Custom field ${custom_attribute.label.toLowerCase()} has been updated`,
        })
      }
    },

    async deleteTask() {
      this.isLoading = true
      try {
        if (this.task_id) {
          await destroy(this.task);
          this.isLoading = false
          window.location.href = this.returnUrl;
        }
      } catch (error) {
        this.isLoading = false
        Swal.fire({
          icon: "error",
          title: "Failed to delete task",
          text: error.message,
        });
      }
    },

    parseTaskCustomAttribute(task) {
      if (task.custom_attributes) {
        this.task.custom_attributes = task.custom_attributes.map(
          (custom_attribute) => {
            return {
              id: custom_attribute.id,
              label: custom_attribute.label,
              type: custom_attribute.type,
              value: custom_attribute.pivot.value,
              options: custom_attribute.options,
              searchable: custom_attribute.searchable,
            }
          }
        )
        this.defaultCustomAttributes = this.task.custom_attributes.slice()
      }
    },
  },

  mounted() {
    this.getAction()
    this.getStaffs()
    this.addCurrentUserToTaskUsers()

    if (this.task_id) {
      this.getTask(this.task_id)
    } else {
      this.assignedType = "individual"
    }

    this.getUserCompanies()
  },
};
</script>
